// Paged-media stylesheet handed to paged.js (passed inline to the Polisher).
//
//   - `size: 11in 8.5in` = Letter LANDSCAPE by explicit dimensions (paged.js's
//     named-size map is lowercase-keyed, so `size: Letter landscape` mis-parsed).
//   - CONTENT rules are scoped under `.pagedjs_page_content` so nothing leaks into
//     the app when paged.js's polisher injects this stylesheet into the document.
//   - `overflow: visible` here is belt-and-suspenders; the real un-clip happens on
//     the SOURCE clone in paginate.js (paged.js can't break content inside a scroll
//     container, so a page-tall table in an overflow-x-auto div silently fails).
//   - the FI Holdings table is 18 columns wide (19 with the on-cost yield toggle on
//     'both'); a slightly smaller font + tighter
//     padding lets it fit landscape without cramming. Numbers/dates keep their
//     natural single line (NO forced word-break, which split "[amount]" -> "350,0 00");
//     only the free-text Description wraps, via its own Tailwind `break-words`.
//
// PER-PAGE HEADERS (so every page reads on its own, like the printed Banyan PDFs):
//   1. Report title block (firm / title / account / date) -> repeated in the top
//      page margin on EVERY page. The block is tagged `.report-running-header` in
//      ReportDisplayModal; paginate.js LIFTS it out of the paginated content and the
//      RepeatReportHeaders handler clones it into each page's `@top-center` margin box.
//      The margin box only exists because `@top-center { content }` is declared, and
//      it is sized by `@page margin-top`, which buildPagedReportCss() below computes
//      from the REAL measured header height (`headerBandPx`, passed in by
//      paginate.js): `72px (0.75in) + headerBandPx` on top vs a flat 0.75in
//      elsewhere, so the band is only as tall as the header actually is instead of a
//      one-size-fits-all guess. Falls back to the old fixed 1.85in when the header
//      can't be measured (headerBandPx <= 0, e.g. absent, or offsetHeight is 0 as in
//      jsdom tests).
//      (We do NOT use paged.js `position: running()`, nor a hidden inline copy: as the
//      flow's first child either one triggers a paged.js bug that emits an empty first
//      page. Removing it from the flow avoids that entirely.)
//   2. Column headers (each table's <thead>) -> paged.js 0.4.3 does NOT repeat a
//      <thead> when a table splits across pages, so the same handler re-clones the
//      original header onto every continuation table. The thead styling below applies
//      to those cloned headers too (they live inside `.pagedjs_page_content`).

export default function buildPagedReportCss(headerBandPx) {
  // 72px == 0.75in at the 96dpi this stylesheet is authored against (consistent
  // with the 11in page == 1056px used throughout paginate.js/PaginatedReportViewer).
  // headerBandPx <= 0 (no header, or an unmeasurable one - offsetHeight is always
  // 0 in jsdom) keeps the old fixed band so unit tests and any edge case stay safe.
  const marginTop = headerBandPx > 0 ? `${72 + headerBandPx}px` : '1.85in';
  return `
@page {
  size: 11in 8.5in;
  /* 0.75in top/bottom, 0.5in left/right: the narrower side margins free ~0.5in
     of content width, and because the table is width:100% with auto layout, the
     surplus flows almost entirely to the Description column - the only column
     whose (wrappable) content is wider than its box; the numeric/date columns
     are nowrap and keep their shrink-to-fit widths. */
  margin: 0.75in 0.5in;
  margin-top: ${marginTop};
  @top-center {
    content: "\\00a0";
  }
  /* Declared so paged.js creates the bottom-right margin box on every page;
     paginate.js stamps the literal page number into it after layout (a CSS
     counter would not survive the detached page clones the viewer displays). */
  @bottom-right {
    content: "\\00a0";
  }
}

/* Small page number, right-aligned in the bottom margin band (the box ends at
   the content's right edge, so flex-end lines the number up with the table). */
.pagedjs_margin-bottom-right .pagedjs_margin-content {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.report-page-number {
  font-size: 9px;
  color: #6b7280;
  text-align: right;
}

/* The margin box that holds the repeated title block (paginate.js lifts the header
   out of the content flow and the handler clones it in here). paged.js makes the box
   a flex container that left-aligns a shrink-to-fit content cell, so force the content
   cell full-width and center it — the title block then centers across the whole page,
   matching the printed report letterhead. Zeroing the header margin drops its on-screen
   bottom gap so it fits the margin band without clipping. */
.pagedjs_margin-top-center {
  padding: 0 !important;
}
/* Fill the margin band exactly and BOTTOM-align the title block inside it
   (justify-content: flex-end): the first table row starts right at the band's
   end, so any band-vs-header measurement slack lands ABOVE the header (reads
   as top margin) instead of as a dead white gap between the header rule and
   the first row. overflow:hidden keeps any excess within the band instead of
   spilling above the sheet edge. */
.pagedjs_margin-top-center .pagedjs_margin-content {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
  overflow: hidden;
}
.pagedjs_margin-top-center .report-running-header {
  display: block !important;
  width: 100%;
  padding: 0 0 2px !important;
}
/* The inner title block carries Tailwind margins that inflate its flex height past
   its offsetHeight; zero all margins in the subtree so the bottom-aligned band
   sits flush (2px guard above keeps glyph descenders clear of overflow:hidden). */
.pagedjs_margin-top-center .report-running-header,
.pagedjs_margin-top-center .report-running-header * {
  margin: 0 !important;
}

.pagedjs_page_content,
.pagedjs_page_content * {
  overflow: visible !important;
  max-height: none !important;
}

.pagedjs_page_content > div { width: 100%; }

.pagedjs_page_content table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9px;
}
.pagedjs_page_content th,
.pagedjs_page_content td {
  padding: 3px 4px;
}
/* FI Holdings (up to 21 columns): one step below the shared 9px base and
   tighter side padding so the Description column - the only wrappable one -
   keeps its w-[210px] preferred width and long security names stay at 2-3
   printed lines. Mirrors index.css. */
.pagedjs_page_content table.fi-holdings-table {
  font-size: 8px;
}
.pagedjs_page_content table.fi-holdings-table th,
.pagedjs_page_content table.fi-holdings-table td {
  padding: 3px 2px;
}
/* Standalone-export safety net (index.css is absent there): row cells start
   on the same top line when a sibling wraps. td-only — th keeps intentional
   alignment (align-bottom rowSpan headers). */
.pagedjs_page_content td {
  vertical-align: top;
}
/* Paged pages are ALWAYS white paper (a printed report), independent of the
   app theme. The report body carries the app's DARK-MODE utilities when dark
   mode is on (it is the DEFAULT theme): body cells inherit 'text-gray-200'
   (near-white), headings/labels use 'text-white', and section bands / totals
   paint 'bg-gray-700' / 'bg-[#141414]'. On a white sheet that renders body rows
   as light-gray-on-white - effectively INVISIBLE - so a full report looks like
   it lost most of its rows while the dark-backgrounded subtotals stay readable.
   Force the paginated surface to the light-mode look (black text on white) so the
   printout matches the verified light-mode rendering regardless of the live theme.

   SCOPE: force black on the content DEFAULT plus every dark-theme GRAY/WHITE text
   utility. We deliberately DO NOT use a blanket '* { color:#000 }', which would
   also flatten the semantic red/green gain-loss text ('text-red-*' / 'text-green-*')
   that other reports rely on - those are left untouched. */
.pagedjs_page_content { color: #000 !important; }
.pagedjs_page_content .text-white,
.pagedjs_page_content .text-gray-100,
.pagedjs_page_content .text-gray-200,
.pagedjs_page_content .text-gray-300,
.pagedjs_page_content .text-gray-400,
.pagedjs_page_content .text-gray-500,
.pagedjs_page_content .text-gray-600,
.pagedjs_page_content .text-gray-700,
.pagedjs_page_content .text-gray-800,
.pagedjs_page_content .text-gray-900 {
  color: #000 !important;
}
/* Neutralize the dark-mode backgrounds Tailwind emits on section bands, section
   totals and the grand-total row so black text on them stays legible. Bands keep
   a light gray fill; totals/grand-total keep their subtle tint. */
/* Realized-gains reports color the Total Gain/Loss headline and every gain cell
   with text-green-600 / text-red-600. The force-black rules above list only the
   gray/white utilities, so these survive - but pin them explicitly so a future
   broadening of the black-on-white scope can't silently flatten the sign color. */
.pagedjs_page_content .text-green-600 { color: #16a34a !important; }
.pagedjs_page_content .text-red-600 { color: #dc2626 !important; }
.pagedjs_page_content .bg-gray-700 { background-color: #e5e7eb !important; }
.pagedjs_page_content .bg-\\[\\#141414\\] { background-color: #f3f4f6 !important; }
.pagedjs_page_content .hover\\:bg-\\[\\#1a1a1a\\]:hover { background-color: transparent !important; }
/* The app draws row/section rules with 'border-white/10' (a near-transparent
   WHITE) in dark mode - invisible on white paper. Give them a light gray so the
   printed table keeps its horizontal rules. */
.pagedjs_page_content .border-white\\/10 { border-color: #d1d5db !important; }
.pagedjs_page_content thead { display: table-header-group; }
/* Keep every bond row WHOLE across a page break: the page ends with the last
   complete row and the next row starts the following page (never a row sliced
   through the middle).

   SCOPE WARNING: these CSS rules only protect paged.js's ELEMENT break path
   (layout.js findOverflow ~559: a node STARTING beyond the page edge; the td /
   tbody checks there read computed style, so these rules do work there). They
   CANNOT stop the TEXT-node split path (~640: a multi-line cell straddling the
   edge), whose only guard is the data-break-inside ATTRIBUTE - paged.js never
   reads CSS on that path and never stamps that attribute itself. paginate.js
   stamps data-break-inside="avoid" on every tr/td of the content clone; that
   stamp, not these rules, is what prevents mid-Description row slicing.
   A row taller than a full page cannot be kept whole and falls back to
   splitting (paged.js's repeated-break-token guard), so this cannot loop. */
.pagedjs_page_content tr { break-inside: avoid; }
.pagedjs_page_content td { break-inside: avoid; }
/* A section title band must never be orphaned at the bottom of a page: the
   renderer wraps each band + its first data row in a section-keep tbody, and
   paged.js honors break-inside:avoid on a rendered tbody (it reads the
   computed style at layout time), pushing the whole unit to the next page.
   NOTE break-before/after rules do NOT work scoped under .pagedjs_page_content
   (paged.js resolves those selectors against the pre-render source tree). */
.pagedjs_page_content tbody.section-keep { break-inside: avoid; }
/* Each bond row lives in its OWN tbody.row-keep so paged.js relocates the whole
   row via its tbody-container branch (dom.js: container = tableRow.parentElement;
   break-inside:avoid on that container -> move the row). NOTE: this covers the
   element break path only (see SCOPE WARNING above) - it did NOT fix the
   mid-Description slicing (that needed the data-break-inside stamp in
   paginate.js). Kept as harmless element-path defence-in-depth. */
.pagedjs_page_content tbody.row-keep { break-inside: avoid; }

/* Billing: each client's daily-MV section starts on a fresh printed page.
   MUST stay UNSCOPED - paged.js resolves break-before/after against the
   pre-render SOURCE tree; scoping under .pagedjs_page_content disables it
   (see NOTE above). NOTE: forced breaks only work because paginate.js stamps
   each element's real computed display alongside its overflow neutralization -
   without an inline display value, paged.js's UndisplayedFilter marks every
   [style] element data-undisplayed and break rules never fire. */
.billing-client-page { break-before: page; }
/* The per-client daily table (31 rows + header) must fit ONE landscape page
   (the printed APX invoice equivalent is one sheet). Shrinking font + padding is
   NOT enough on its own: Tailwind's text-xs on the table pins line-height to
   16px, which this rule does not override, so each row stayed ~19px tall and 31
   rows overflowed onto 3 pages (an orphaned heading + split table). Pinning a
   tight line-height here is what actually collapses the row pitch to ~13px and
   buys the room. Scoped: applies to the rendered pages paged.js lays out
   against, so the chunker measures the compact rows. */
.pagedjs_page_content table.billing-daily { font-size: 9px; }
.pagedjs_page_content table.billing-daily th,
.pagedjs_page_content table.billing-daily td { padding: 1px 6px; line-height: 1.1; }

/* FI Distribution "Summary Information" is built from card DIVs, not a <table>,
   so the 'table { font-size: 9px }' rule above never reaches it: on paper it kept
   its full 16-20px on-screen size while every distribution table around it is 9px,
   so the summary towered over the rest of the report. Shrink the card body to the
   9px table body size and tighten the card chrome so it reads as the same report.
   The section <h3> ("Summary Information") is intentionally left untouched — it
   already matches the other section headers ("Distribution by ..."). On screen
   (un-paginated) the .report-kv-summary hook is inert, so the modal is unchanged. */
.pagedjs_page_content .report-kv-summary { gap: 14px; }
.pagedjs_page_content .report-kv-summary > div { padding: 6px 9px; }
.pagedjs_page_content .report-kv-summary h4 { font-size: 11px; }
.pagedjs_page_content .report-kv-summary > div > h4,
.pagedjs_page_content .report-kv-summary > div > div:first-child { margin-bottom: 4px; padding-bottom: 3px; }
.pagedjs_page_content .report-kv-summary .space-y-2 { font-size: 9px; }
.pagedjs_page_content .report-kv-summary .space-y-2 > * + * { margin-top: 2px; }
`;
}
